import { Typography } from "@mui/material";
import * as _ from "lodash";
import { ReactElement } from "react";
import { FormattedMessage } from "react-intl";

import { TexterCampaignSendStatus } from "~frontend/__generated__/graphql";
import FormattedDateTime from "~frontend/components/FormattedDateTime";

import {
  CampaignInboxSummary,
  EscalationInboxDetails,
  GeneralInboxDetails,
} from "./types";

export function InboxCampaignSummary({
  showTexterSendInfo,
  campaign,
}:
  | {
      showTexterSendInfo: true;
      campaign:
        | CampaignInboxSummary["campaign"]
        | GeneralInboxDetails["statsByCampaign"][number]["campaign"];
    }
  | {
      showTexterSendInfo: false;
      campaign: EscalationInboxDetails["statsByCampaign"][number]["campaign"];
    }): ReactElement {
  if (!campaign) {
    // This can happen when a thread which has never been replied to
    // by a contact gets moved into an escalation or general inbox.
    return (
      <>
        <Typography variant="body1" component="p" sx={{ paddingBottom: "4px" }}>
          <FormattedMessage
            defaultMessage="No associated campaign"
            description="Label for a thread without an owning campaign"
          />
        </Typography>
      </>
    );
  }
  const yourInitialsSent = _.chain(campaign.myCampaignSends)
    .filter(send => send.status === TexterCampaignSendStatus.Complete)
    .sumBy(send => send.batchSize)
    .value();

  return (
    <>
      <Typography variant="body1" component="p" sx={{ paddingBottom: "4px" }}>
        {campaign.name}
      </Typography>
      <Typography
        variant="body3"
        component="p"
        sx={{
          paddingBottom: "4px",
          lineHeight: "14px",
        }}
      >
        {campaign.description}
      </Typography>
      <Typography variant="body3" component="p">
        <FormattedMessage
          defaultMessage="Started: {startedAt}"
          description="The date and time a campaign started"
          values={{
            startedAt: <FormattedDateTime value={campaign.startedAt} />,
          }}
        />
      </Typography>
      {showTexterSendInfo && campaign.texterSendEnabled && yourInitialsSent && (
        <Typography variant="body3" component="p">
          <FormattedMessage
            defaultMessage="Your Initials Sent: {yourInitialsSent}"
            description="The count of initials a texter has sent"
            values={{
              yourInitialsSent: yourInitialsSent.toLocaleString(),
            }}
          />
        </Typography>
      )}
      {showTexterSendInfo && campaign.texterSendEnabled && (
        <Typography variant="body3" component="p">
          <FormattedMessage
            defaultMessage="Send Progress: {sendProgress}%"
            description="The send progress for a campaign"
            values={{
              sendProgress: Math.round(campaign.sendProgress * 100),
            }}
          />
        </Typography>
      )}
    </>
  );
}
